<template>
  <div class="landing-page">
    <header class="site-header" :class="{ 'is-scrolled': isScrolled }">
      <div class="container nav-shell">
        <a class="brand" href="#top" aria-label="耕耘未来首页">
          <img src="@/assets/images/image1.png" alt="耕耘未来">
        </a>

        <nav class="desktop-nav" aria-label="主导航">
          <a href="#features">产品能力</a>
          <a href="#carriers">快递渠道</a>
          <a href="#advantages">平台优势</a>
          <a href="#pricing">价格方案</a>
          <a href="#process">交付流程</a>
        </nav>

        <div class="nav-actions">
          <button class="text-button desktop-only" type="button" @click="scrollToSection('contact')">
            合作咨询
          </button>
          <button class="primary-button nav-cta" type="button" @click="openConsult">
            <WechatOutlined />
            立即咨询
          </button>
          <button
            class="icon-button menu-button"
            type="button"
            :aria-label="menuOpen ? '关闭菜单' : '打开菜单'"
            :aria-expanded="menuOpen"
            @click="menuOpen = !menuOpen"
          >
            <CloseOutlined v-if="menuOpen" />
            <MenuOutlined v-else />
          </button>
        </div>
      </div>

      <transition name="menu">
        <div v-if="menuOpen" class="mobile-menu">
          <a href="#features" @click="closeMenu">产品能力</a>
          <a href="#carriers" @click="closeMenu">快递渠道</a>
          <a href="#advantages" @click="closeMenu">平台优势</a>
          <a href="#pricing" @click="closeMenu">价格方案</a>
          <a href="#process" @click="closeMenu">交付流程</a>
          <button type="button" @click="openConsult">合作咨询</button>
        </div>
      </transition>
    </header>

    <main id="top">
      <section class="hero-section">
        <div class="container hero-grid">
          <div class="hero-copy">
            <div class="eyebrow">
              <span class="eyebrow-dot"></span>
              快递 CPS 私有化解决方案
            </div>
            <h1>
              <span>一个后台，</span>
              <span>聚合全网寄件渠道</span>
            </h1>
            <p class="hero-description">
              聚合渠道、智能比价、统一下单与独立部署，帮助企业快速搭建自主可控的快递服务平台。
            </p>
            <div class="hero-actions">
              <button class="primary-button hero-primary" type="button" @click="openConsult">
                开始使用
                <ArrowRightOutlined />
              </button>
              <button class="secondary-button" type="button" @click="scrollToSection('features')">
                了解产品
              </button>
            </div>
            <div class="hero-proof" aria-label="产品特点">
              <span><CheckCircleFilled /> 多渠道聚合</span>
              <span><CheckCircleFilled /> AI 智能比价</span>
              <span><CheckCircleFilled /> 数据自主掌控</span>
            </div>
          </div>

          <div class="hero-visual">
            <div class="visual-orbit orbit-one"></div>
            <div class="visual-orbit orbit-two"></div>
            <img src="@/assets/images/index.png" alt="快递 CPS 管理后台与用户端小程序">
          </div>
        </div>

        <div class="container hero-stats">
          <div>
            <strong>9+</strong>
            <span>主流快递渠道</span>
          </div>
          <div>
            <strong>60%</strong>
            <span>最高推广返佣</span>
          </div>
          <div>
            <strong>100%</strong>
            <span>专业版源码交付</span>
          </div>
          <div>
            <strong>7×24h</strong>
            <span>稳定业务运行</span>
          </div>
        </div>
      </section>

      <section id="carriers" class="carrier-section section">
        <div class="container">
          <div class="section-heading split-heading">
            <div>
              <span class="section-kicker">01 / 快递网络</span>
              <h2>一次接入，灵活选择多家快递</h2>
            </div>
            <p>覆盖主流快递、同城急送与大件物流，让不同寄件场景都有合适方案。</p>
          </div>

          <div class="carrier-grid">
            <div v-for="carrier in carriers" :key="carrier.name" class="carrier-item">
              <img :src="carrier.logo" :alt="carrier.name">
              <span>{{ carrier.name }}</span>
            </div>
          </div>
        </div>
      </section>

      <section id="features" class="feature-section section">
        <div class="container">
          <div class="section-heading centered-heading">
            <span class="section-kicker">02 / 核心能力</span>
            <h2>一套系统，覆盖业务增长全链路</h2>
            <p>从下单履约到营销增长，再到数据安全，一套系统覆盖核心经营链路。</p>
          </div>

          <div class="feature-grid">
            <article class="feature-card feature-card-large feature-card-blue">
              <div class="feature-icon"><RobotOutlined /></div>
              <span class="feature-index">01</span>
              <div class="feature-content">
                <h3>智能 AI 比价</h3>
                <p>聚合多家寄件平台，根据线路、重量与时效自动筛选更优渠道，持续降低寄件成本。</p>
              </div>
              <div class="mini-chart" aria-hidden="true">
                <span style="height: 38%"></span>
                <span style="height: 58%"></span>
                <span style="height: 48%"></span>
                <span style="height: 76%"></span>
                <span style="height: 92%"></span>
              </div>
            </article>

            <article class="feature-card feature-card-large feature-card-dark">
              <div class="feature-icon"><CloudServerOutlined /></div>
              <span class="feature-index">02</span>
              <div class="feature-content">
                <h3>私有化部署</h3>
                <p>系统部署在您的服务器，用户、订单和经营数据独立留存，业务资产真正掌握在自己手中。</p>
              </div>
              <div class="deployment-status">
                <span><i></i> 服务运行稳定</span>
                <strong>99.99%</strong>
              </div>
            </article>

            <article v-for="feature in features" :key="feature.title" class="feature-card feature-card-small">
              <div class="feature-icon">
                <component :is="feature.icon" />
              </div>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
            </article>
          </div>
        </div>
      </section>

      <section id="advantages" class="advantage-section section">
        <div class="container advantage-layout">
          <div class="advantage-intro">
            <span class="section-kicker light">03 / 平台价值</span>
            <h2>
              <span>不止是系统交付</span>
              <span>更是持续经营的底座</span>
            </h2>
            <p>围绕增长、稳定与长期服务构建产品能力，让快递业务更容易启动，也更容易持续。</p>
            <button class="light-button" type="button" @click="openConsult">
              获取解决方案
              <ArrowRightOutlined />
            </button>
          </div>

          <div class="advantage-list">
            <article v-for="(advantage, index) in advantages" :key="advantage.title">
              <span>0{{ index + 1 }}</span>
              <div>
                <h3>{{ advantage.title }}</h3>
                <p>{{ advantage.description }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="pricing" class="pricing-section section">
        <div class="container">
          <div class="section-heading split-heading">
            <div>
              <span class="section-kicker">04 / 价格方案</span>
              <h2>选择适合当前阶段的交付方案</h2>
            </div>
            <p>支持授权部署、永久源码交付与定制商用方案，满足不同规模的项目需求。</p>
          </div>

          <div class="pricing-grid">
            <article v-for="plan in plans" :key="plan.name" class="pricing-card" :class="{ featured: plan.featured }">
              <div v-if="plan.featured" class="recommend-tag">推荐方案</div>
              <div class="plan-head">
                <span>{{ plan.label }}</span>
                <h3>{{ plan.name }}</h3>
                <div class="plan-price" :class="{ custom: !plan.price }">
                  <template v-if="plan.price">
                    <small>¥</small>{{ plan.price }}<em>/ {{ plan.unit }}</em>
                  </template>
                  <template v-else>按需定制</template>
                </div>
                <p>{{ plan.summary }}</p>
              </div>
              <ul>
                <li v-for="item in plan.items" :key="item">
                  <CheckOutlined />
                  <span>{{ item }}</span>
                </li>
              </ul>
              <button :class="plan.featured ? 'primary-button' : 'secondary-button'" type="button" @click="openConsult">
                {{ plan.action }}
                <ArrowRightOutlined />
              </button>
            </article>
          </div>
        </div>
      </section>

      <section id="process" class="process-section section">
        <div class="container">
          <div class="section-heading centered-heading">
            <span class="section-kicker">05 / 交付流程</span>
            <h2>需求确认后，四步完成交付</h2>
            <p>流程清晰、专人跟进，从商务沟通到系统上线全程协同。</p>
          </div>

          <div class="process-list">
            <article v-for="(step, index) in processSteps" :key="step.title">
              <div class="step-number">0{{ index + 1 }}</div>
              <div class="step-line"></div>
              <div class="step-icon">
                <component :is="step.icon" />
              </div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.description }}</p>
            </article>
          </div>
        </div>
      </section>

      <section id="contact" class="cooperation-section section">
        <div class="container cooperation-panel">
          <div class="cooperation-copy">
            <span class="section-kicker light">合作计划</span>
            <h2>开放合作与代理招募</h2>
            <p>无论您拥有公众号、小程序、App 流量入口，还是正在寻找长期副业项目，都可以接入快递 CPS 服务，共享业务增长。</p>
            <div class="cooperation-points">
              <span><CheckCircleFilled /> 全网最高返佣 60%</span>
              <span><CheckCircleFilled /> 提供系统培训与项目陪跑</span>
            </div>
            <button class="light-button" type="button" @click="openConsult">
              联系商务
              <ArrowRightOutlined />
            </button>
          </div>
          <img src="@/assets/images/it-support-monochromatic.png" alt="商务客服支持">
        </div>
      </section>

      <section class="trust-section section">
        <div class="container">
          <div class="business-data">
            <div v-for="item in businessData" :key="item.label">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}<small>{{ item.unit }}</small></strong>
            </div>
          </div>

          <div class="trust-grid">
            <div class="qualification-block">
              <div class="section-heading">
                <span class="section-kicker">06 / 企业资质</span>
                <h2>自主研发，资质可查</h2>
                <p>拥有快递 CPS 系统相关软件著作权，持续迭代产品，为项目长期运营提供保障。</p>
              </div>
              <div class="certificate-list">
                <button
                  v-for="certificate in certificates"
                  :key="certificate.alt"
                  type="button"
                  @click="previewCertificate = certificate"
                >
                  <img :src="certificate.src" :alt="certificate.alt">
                  <span>点击查看大图</span>
                </button>
              </div>
            </div>

            <div class="partner-block">
              <span class="section-kicker">合作伙伴</span>
              <h3>平台合作伙伴</h3>
              <p>与主流快递服务体系协同，为不同寄件场景提供稳定支持。</p>
              <div class="partner-grid">
                <div v-for="partner in partners" :key="partner.name">
                  <img :src="partner.logo" :alt="partner.name">
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="container footer-main">
        <div class="footer-brand">
          <img src="@/assets/images/image1.png" alt="耕耘未来">
          <p>专注快递 CPS 系统研发与私有化交付，帮助企业构建长期、稳定、自主的寄件业务。</p>
        </div>
        <div class="footer-links">
          <div>
            <h3>产品</h3>
            <a href="#features">产品能力</a>
            <a href="#carriers">快递渠道</a>
            <a href="#pricing">价格方案</a>
          </div>
          <div>
            <h3>服务</h3>
            <a href="#process">交付流程</a>
            <a href="#advantages">平台优势</a>
            <button type="button" @click="openConsult">合作咨询</button>
          </div>
          <div class="footer-contact">
            <h3>联系我们</h3>
            <span>电话 / 微信</span>
            <strong>15116476625</strong>
            <span>湖南耕耘未来科技有限公司</span>
          </div>
        </div>
        <div class="footer-code">
          <div class="footer-code-card">
            <img src="@/assets/images/qrcode2.png" alt="商务咨询二维码">
          </div>
          <span>微信扫码咨询</span>
        </div>
      </div>
      <div class="container footer-bottom">
        <span>Copyright © 2023 - 2026 kdcps. 耕耘未来科技版权所有</span>
        <a href="https://beian.miit.gov.cn" target="_blank" rel="noreferrer">湘ICP备2023025772号-3</a>
      </div>
    </footer>

    <div class="floating-actions">
      <button class="consult-fab" type="button" aria-label="打开商务咨询" @click="openConsult">
        <WechatOutlined />
        <span>咨询</span>
      </button>
      <button v-if="isScrolled" class="top-fab" type="button" aria-label="返回顶部" @click="goTop">
        <ArrowUpOutlined />
      </button>
    </div>

    <a-modal
      v-model:open="consultVisible"
      :footer="null"
      :width="420"
      centered
      wrap-class-name="consult-modal"
    >
      <div class="consult-content">
        <span class="modal-kicker">BUSINESS CONSULTING</span>
        <h2>扫码添加商务顾问</h2>
        <p>沟通项目场景、部署方式与适合您的合作方案。</p>
        <div class="modal-code">
          <img src="@/assets/images/qrcode2.png" alt="商务咨询二维码">
        </div>
        <div class="modal-phone">
          <PhoneOutlined />
          <span>15116476625</span>
        </div>
      </div>
    </a-modal>

    <a-modal
      :open="Boolean(previewCertificate)"
      :footer="null"
      :width="620"
      centered
      @cancel="previewCertificate = null"
    >
      <img
        v-if="previewCertificate"
        class="certificate-preview"
        :src="previewCertificate.src"
        :alt="previewCertificate.alt"
      >
    </a-modal>
  </div>
</template>

<script setup>
import { markRaw, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  CloudServerOutlined,
  CodeOutlined,
  FileProtectOutlined,
  LockOutlined,
  MenuOutlined,
  MessageOutlined,
  PhoneOutlined,
  RobotOutlined,
  ShopOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WechatOutlined
} from '@ant-design/icons-vue'

import shunfeng from '@/assets/images/shunfeng.png'
import debang from '@/assets/images/debang.png'
import jingdong from '@/assets/images/jingdong.png'
import yuantong from '@/assets/images/yuantong.png'
import shentong from '@/assets/images/shentong.png'
import zhongtong from '@/assets/images/zhongtong.png'
import yunda from '@/assets/images/yunda.png'
import jitu from '@/assets/images/jitu.png'
import shansong from '@/assets/images/shansong.png'
import certificateOne from '@/assets/images/Rectangle1.png'
import certificateTwo from '@/assets/images/Rectangle2.png'
import partnerSf from '@/assets/images/sf.png'
import partnerJd from '@/assets/images/jd.png'
import partnerYt from '@/assets/images/yt.png'
import partnerDb from '@/assets/images/db.png'
import partnerJt from '@/assets/images/jt.jpg'
import partnerYd from '@/assets/images/yd.png'
import partnerSt from '@/assets/images/st.png'
import partnerZt from '@/assets/images/zt.png'

const consultVisible = ref(false)
const previewCertificate = ref(null)
const menuOpen = ref(false)
const isScrolled = ref(false)

const carriers = [
  { name: '顺丰快递', logo: shunfeng },
  { name: '德邦快递', logo: debang },
  { name: '京东物流', logo: jingdong },
  { name: '圆通速递', logo: yuantong },
  { name: '申通快递', logo: shentong },
  { name: '中通快递', logo: zhongtong },
  { name: '韵达快递', logo: yunda },
  { name: '极兔速递', logo: jitu },
  { name: '闪送', logo: shansong }
]

const features = [
  {
    icon: markRaw(ShopOutlined),
    title: '多端统一下单',
    description: '支持微信公众号、微信小程序等入口，快递、同城急送和大件物流统一承接。'
  },
  {
    icon: markRaw(ApartmentOutlined),
    title: '分销裂变增长',
    description: '内置推广员、分佣与优惠券能力，帮助平台拓客、留存并形成持续增长。'
  },
  {
    icon: markRaw(LockOutlined),
    title: '系统稳定安全',
    description: '独立环境部署与权限管理机制，保障订单数据、用户数据和经营信息安全。'
  },
  {
    icon: markRaw(ThunderboltOutlined),
    title: '高并发高性能',
    description: '面向真实业务场景优化核心链路，保持高峰期下单与查询体验稳定顺畅。'
  }
]

const advantages = [
  {
    title: '成熟的营销与分销能力',
    description: '从优惠券到推广分佣，围绕私域场景搭建完整增长链路，降低获客与转化难度。'
  },
  {
    title: '数据和业务资产独立',
    description: '支持私有化部署，用户、订单和经营数据专属于您，为长期运营建立稳定基础。'
  },
  {
    title: '自主研发与持续服务',
    description: '拥有相关软件著作权，提供部署指导、使用培训与项目陪跑，交付更有保障。'
  }
]

const plans = [
  {
    label: '适合项目起步',
    name: '基础套餐',
    price: '29,999',
    unit: '年',
    summary: '适合个人或企业快速搭建自有快递 CPS 项目。',
    items: ['私有化部署至您的服务器', '一对一部署与项目指导', '一年免费程序更新', '1 个授权码 / 1 台机器'],
    action: '咨询基础版'
  },
  {
    label: '适合长期运营',
    name: '专业套餐',
    price: '99,999',
    unit: '永久',
    summary: '适合企业长期运营，并需要完整源码自主维护。',
    items: ['交付 100% 项目源码', '支持内部功能调整', '6 个月免费工单支持', '提供部署文档与使用手册'],
    action: '咨询专业版',
    featured: true
  },
  {
    label: '适合商业集成',
    name: '商用套餐',
    summary: '适合接入现有 App、系统或需要定制开发的项目。',
    items: ['按业务场景定制开发', '支持接入现有产品体系', '可二次开发并商业化', '专属解决方案与交付计划'],
    action: '获取商用方案'
  }
]

const processSteps = [
  {
    icon: markRaw(MessageOutlined),
    title: '需求沟通',
    description: '添加商务顾问，确认业务场景、功能范围与部署需求。'
  },
  {
    icon: markRaw(FileProtectOutlined),
    title: '签约付款',
    description: '签订合同，支持对公打款、微信或支付宝等付款方式。'
  },
  {
    icon: markRaw(CodeOutlined),
    title: '系统部署',
    description: '完成授权、服务器配置、系统搭建与必要的功能调整。'
  },
  {
    icon: markRaw(TeamOutlined),
    title: '培训交付',
    description: '提供使用培训、部署资料与项目陪跑，协助业务顺利上线。'
  }
]

const businessData = [
  { label: '日下单量', value: '378', unit: '单+' },
  { label: '日下单金额', value: '1527', unit: '元+' },
  { label: '活跃用户', value: '1532', unit: '人+' },
  { label: '会员总数', value: '12000', unit: '人+' }
]

const certificates = [
  { src: certificateOne, alt: '快递 CPS 系统软件著作权证书' },
  { src: certificateTwo, alt: '相关系统软件著作权证书' }
]

const partners = [
  { name: '顺丰', logo: partnerSf },
  { name: '京东物流', logo: partnerJd },
  { name: '圆通速递', logo: partnerYt },
  { name: '德邦快递', logo: partnerDb },
  { name: '极兔速递', logo: partnerJt },
  { name: '韵达快递', logo: partnerYd },
  { name: '申通快递', logo: partnerSt },
  { name: '中通快递', logo: partnerZt }
]

const openConsult = () => {
  menuOpen.value = false
  consultVisible.value = true
}

const closeMenu = () => {
  menuOpen.value = false
}

const scrollToSection = (id) => {
  menuOpen.value = false
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const goTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 40
}

onMounted(() => {
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped lang="scss">
:global(*) {
  box-sizing: border-box;
}

:global(html) {
  scroll-behavior: smooth;
}

:global(body) {
  margin: 0;
  min-width: 320px;
  background: #ffffff;
  color: #10203a;
  font-family: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

:global(button),
:global(a) {
  font: inherit;
}

:global(button) {
  border: 0;
}

:global(a) {
  color: inherit;
  text-decoration: none;
}

:global(#app) {
  min-height: 100vh;
}

.landing-page {
  --ink: #0c1d35;
  --muted: #65758b;
  --line: #dbe3ed;
  --surface: #f5f7fa;
  --blue: #1b63dc;
  --blue-dark: #124cad;
  --navy: #061a34;
  --cyan: #35b9e9;
  overflow: hidden;
  background: #fff;
}

.container {
  width: min(1360px, calc(100% - 64px));
  margin: 0 auto;
}

.section {
  padding: 124px 0;
  scroll-margin-top: 80px;
}

.site-header {
  position: fixed;
  z-index: 100;
  top: 0;
  right: 0;
  left: 0;
  height: 80px;
  border-bottom: 1px solid transparent;
  transition: background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
}

.site-header.is-scrolled {
  border-color: rgba(16, 32, 58, 0.08);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 10px 30px rgba(24, 54, 95, 0.06);
  backdrop-filter: blur(16px);
}

.nav-shell {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: flex;
  width: 152px;
  align-items: center;
}

.brand img {
  display: block;
  width: 100%;
  height: auto;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 38px;
}

.desktop-nav a {
  position: relative;
  color: #405069;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;
}

.desktop-nav a::after {
  position: absolute;
  right: 0;
  bottom: -10px;
  left: 0;
  height: 2px;
  background: var(--blue);
  content: "";
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.2s ease;
}

.desktop-nav a:hover {
  color: var(--blue);
}

.desktop-nav a:hover::after {
  transform: scaleX(1);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.text-button {
  padding: 10px 8px;
  background: transparent;
  color: #40506a;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.primary-button,
.secondary-button,
.light-button {
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  white-space: nowrap;
}

.primary-button {
  background: var(--blue);
  box-shadow: 0 12px 26px rgba(31, 102, 229, 0.2);
  color: #fff;
}

.primary-button:hover {
  background: var(--blue-dark);
  box-shadow: 0 16px 32px rgba(31, 102, 229, 0.26);
  transform: translateY(-2px);
}

.primary-button:active,
.secondary-button:active,
.light-button:active {
  transform: translateY(1px);
}

.secondary-button {
  border: 1px solid #cdd8e6;
  background: #fff;
  color: var(--ink);
}

.secondary-button:hover {
  border-color: var(--blue);
  color: var(--blue);
  transform: translateY(-2px);
}

.light-button {
  padding: 0 22px;
  background: #fff;
  color: var(--navy);
}

.light-button:hover {
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.16);
  transform: translateY(-2px);
}

.nav-cta {
  min-height: 44px;
  padding: 0 20px;
  font-size: 14px;
}

.icon-button {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  color: var(--ink);
  cursor: pointer;
  font-size: 19px;
}

.menu-button,
.mobile-menu {
  display: none;
}

.hero-section {
  position: relative;
  min-height: 820px;
  padding: 142px 0 0;
  background: #f3f7fc;
}

.hero-section::before {
  position: absolute;
  top: 80px;
  bottom: 0;
  left: 50%;
  width: 1px;
  background: rgba(43, 91, 155, 0.08);
  content: "";
}

.hero-grid {
  display: grid;
  min-height: 580px;
  align-items: center;
  grid-template-columns: minmax(600px, 0.95fr) minmax(0, 1.05fr);
  gap: 0;
}

.hero-copy {
  position: relative;
  z-index: 2;
  padding: 0 0 40px 12px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 27px;
  color: var(--blue);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
}

.eyebrow-dot {
  position: relative;
  width: 10px;
  height: 10px;
  border: 3px solid #cfe1ff;
  border-radius: 50%;
  background: var(--blue);
  box-shadow: 0 0 0 4px rgba(31, 102, 229, 0.08);
}

.hero-copy h1 {
  margin: 0;
  color: var(--ink);
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: 68px;
  font-weight: 800;
  line-height: 1.16;
  letter-spacing: 0;
}

.hero-copy h1 span {
  display: block;
  white-space: nowrap;
}

.hero-description {
  max-width: 590px;
  margin: 27px 0 0;
  color: #526278;
  font-size: 17px;
  line-height: 1.9;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 32px;
}

.hero-actions button {
  min-width: 132px;
  padding: 0 24px;
}

.hero-primary {
  min-width: 146px !important;
}

.hero-proof {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 27px;
}

.hero-proof span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #526279;
  font-size: 13px;
  font-weight: 600;
}

.hero-proof .anticon {
  color: var(--blue);
}

.hero-visual {
  position: relative;
  min-height: 580px;
}

.hero-visual > img {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  width: min(860px, 58vw);
  max-width: none;
  filter: drop-shadow(0 28px 38px rgba(24, 57, 105, 0.14));
  transform: translate(-47%, -49%);
  animation: visual-enter 0.9s ease both;
}

.visual-orbit {
  position: absolute;
  border: 1px solid rgba(31, 102, 229, 0.12);
  border-radius: 50%;
}

.orbit-one {
  top: 6%;
  right: 3%;
  width: 470px;
  height: 470px;
}

.orbit-two {
  top: 23%;
  right: 17%;
  width: 320px;
  height: 320px;
}

.hero-stats {
  position: relative;
  z-index: 4;
  display: grid;
  min-height: 112px;
  padding: 0 48px;
  border: 1px solid rgba(194, 211, 232, 0.85);
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  background: #fff;
  box-shadow: 0 -16px 40px rgba(39, 74, 118, 0.05);
  grid-template-columns: repeat(4, 1fr);
}

.hero-stats > div {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.hero-stats > div:not(:last-child)::after {
  position: absolute;
  top: 33px;
  right: 0;
  width: 1px;
  height: 52px;
  background: var(--line);
  content: "";
}

.hero-stats strong {
  color: var(--ink);
  font-size: 28px;
  font-weight: 800;
}

.hero-stats span {
  margin-top: 7px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.section-heading h2,
.advantage-intro h2,
.cooperation-copy h2 {
  margin: 15px 0 0;
  color: var(--ink);
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: 46px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0;
}

.section-heading p {
  margin: 18px 0 0;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.8;
}

.section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--blue);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
}

.section-kicker::before {
  width: 26px;
  height: 2px;
  background: currentColor;
  content: "";
}

.section-kicker.light {
  color: #78c9ff;
}

.split-heading {
  display: grid;
  align-items: end;
  grid-template-columns: 1.35fr 0.65fr;
  gap: 110px;
}

.split-heading p {
  margin-bottom: 4px;
}

.centered-heading {
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
}

.carrier-section {
  border-bottom: 1px solid #e4ebf3;
  background: #fff;
}

.carrier-grid {
  display: grid;
  margin-top: 64px;
  border-top: 1px solid var(--line);
  border-left: 1px solid var(--line);
  grid-template-columns: repeat(5, 1fr);
}

.carrier-item {
  display: flex;
  min-height: 146px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  background: #fff;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.carrier-item:hover {
  z-index: 2;
  background: #f8fbff;
  box-shadow: 0 18px 40px rgba(31, 68, 115, 0.08);
  transform: translateY(-3px);
}

.carrier-item img {
  width: 98px;
  height: 68px;
  object-fit: contain;
}

.carrier-item span {
  color: #33445e;
  font-size: 13px;
  font-weight: 700;
}

.feature-section {
  background: var(--surface);
}

.feature-grid {
  display: grid;
  margin-top: 68px;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
}

.feature-card {
  position: relative;
  min-height: 276px;
  overflow: hidden;
  padding: 30px;
  border: 1px solid #dce5f0;
  border-radius: 8px;
  background: #fff;
}

.feature-card-large {
  min-height: 386px;
  grid-column: span 2;
}

.feature-card-small {
  min-height: 245px;
  padding: 30px 8px 12px;
  border: 0;
  border-top: 1px solid #cfd9e5;
  border-radius: 0;
  background: transparent;
}

.feature-card-small .feature-icon {
  width: 42px;
  height: 42px;
  background: #fff;
  font-size: 20px;
}

.feature-card-small h3 {
  margin-top: 25px;
}

.feature-card-small p {
  max-width: 270px;
}

.feature-card-blue {
  border-color: #cfe0fa;
  background: #eaf3ff;
}

.feature-card-dark {
  border-color: #132e54;
  background: var(--navy);
}

.feature-icon {
  display: inline-flex;
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d7e4f4;
  border-radius: 8px;
  background: #eef5ff;
  color: var(--blue);
  font-size: 22px;
}

.feature-card-large .feature-icon {
  background: rgba(255, 255, 255, 0.75);
}

.feature-card-dark .feature-icon {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.08);
  color: #68c8ff;
}

.feature-index {
  position: absolute;
  top: 31px;
  right: 30px;
  color: rgba(16, 32, 58, 0.25);
  font-size: 13px;
  font-weight: 800;
}

.feature-card-dark .feature-index {
  color: rgba(255, 255, 255, 0.35);
}

.feature-content {
  position: absolute;
  bottom: 30px;
  left: 30px;
  max-width: 57%;
}

.feature-card h3 {
  margin: 24px 0 0;
  color: var(--ink);
  font-size: 20px;
  font-weight: 800;
}

.feature-card p {
  margin: 13px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.75;
}

.feature-card-large h3 {
  margin-top: 0;
  font-size: 26px;
}

.feature-card-dark h3 {
  color: #fff;
}

.feature-card-dark p {
  color: #aebdd0;
}

.mini-chart {
  position: absolute;
  right: 28px;
  bottom: 29px;
  display: flex;
  width: 156px;
  height: 122px;
  align-items: flex-end;
  gap: 9px;
  padding: 18px;
  border: 1px solid rgba(31, 102, 229, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.64);
}

.mini-chart span {
  width: 16px;
  border-radius: 3px 3px 0 0;
  background: var(--blue);
  opacity: 0.85;
}

.deployment-status {
  position: absolute;
  right: 30px;
  bottom: 30px;
  width: 165px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.deployment-status span {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #aebdd0;
  font-size: 11px;
}

.deployment-status i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #45d79b;
  box-shadow: 0 0 0 4px rgba(69, 215, 155, 0.12);
}

.deployment-status strong {
  display: block;
  margin-top: 11px;
  color: #fff;
  font-size: 28px;
}

.advantage-section {
  position: relative;
  overflow: hidden;
  background: var(--navy);
}

.advantage-section::before {
  position: absolute;
  top: -180px;
  right: -160px;
  width: 520px;
  height: 520px;
  border: 1px solid rgba(89, 170, 255, 0.12);
  border-radius: 50%;
  content: "";
}

.advantage-layout {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(500px, 0.92fr) minmax(0, 1.08fr);
  gap: 130px;
}

.advantage-intro h2 {
  color: #fff;
  font-size: 52px;
  line-height: 1.34;
}

.advantage-intro h2 span {
  display: block;
  white-space: nowrap;
}

.advantage-intro > p {
  max-width: 500px;
  margin: 24px 0 38px;
  color: #aebdd0;
  font-size: 16px;
  line-height: 1.85;
}

.advantage-list {
  border-top: 1px solid rgba(255, 255, 255, 0.14);
}

.advantage-list article {
  display: grid;
  padding: 34px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  grid-template-columns: 58px 1fr;
  gap: 22px;
}

.advantage-list article > span {
  color: #5bbaff;
  font-size: 13px;
  font-weight: 800;
}

.advantage-list h3 {
  margin: 0;
  color: #fff;
  font-size: 21px;
}

.advantage-list p {
  margin: 11px 0 0;
  color: #9fb0c5;
  font-size: 14px;
  line-height: 1.8;
}

.pricing-section {
  background: #fff;
}

.pricing-grid {
  display: grid;
  margin-top: 68px;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.pricing-card {
  position: relative;
  display: flex;
  min-height: 560px;
  padding: 34px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  flex-direction: column;
}

.pricing-card.featured {
  border-color: var(--blue);
  box-shadow: 0 24px 60px rgba(31, 102, 229, 0.13);
}

.recommend-tag {
  position: absolute;
  top: 0;
  right: 26px;
  padding: 8px 13px;
  border-radius: 0 0 6px 6px;
  background: var(--blue);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.plan-head > span {
  color: var(--blue);
  font-size: 12px;
  font-weight: 800;
}

.plan-head h3 {
  margin: 13px 0 0;
  color: var(--ink);
  font-size: 26px;
}

.plan-price {
  min-height: 57px;
  margin-top: 24px;
  color: var(--ink);
  font-size: 40px;
  font-weight: 800;
  line-height: 1.15;
}

.plan-price small {
  margin-right: 3px;
  font-size: 18px;
}

.plan-price em {
  margin-left: 6px;
  color: var(--muted);
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
}

.plan-price.custom {
  display: flex;
  align-items: center;
  color: var(--blue);
  font-size: 34px;
}

.plan-head p {
  min-height: 52px;
  margin: 18px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.75;
}

.pricing-card ul {
  display: grid;
  margin: 28px 0 32px;
  padding: 28px 0 0;
  border-top: 1px solid var(--line);
  list-style: none;
  gap: 16px;
}

.pricing-card li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #465770;
  font-size: 14px;
  line-height: 1.55;
}

.pricing-card li .anticon {
  margin-top: 3px;
  color: var(--blue);
}

.pricing-card button {
  width: 100%;
  margin-top: auto;
}

.process-section {
  background: var(--surface);
}

.process-list {
  display: grid;
  margin-top: 76px;
  grid-template-columns: repeat(4, 1fr);
}

.process-list article {
  position: relative;
  padding-right: 34px;
}

.step-number {
  color: #8ca0b8;
  font-size: 12px;
  font-weight: 800;
}

.step-line {
  position: absolute;
  top: 52px;
  right: 0;
  left: 68px;
  height: 1px;
  background: #cbd7e5;
}

.process-list article:last-child .step-line {
  display: none;
}

.step-icon {
  display: flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  border: 1px solid #ccdaeb;
  border-radius: 8px;
  background: #fff;
  color: var(--blue);
  font-size: 23px;
  box-shadow: 0 10px 25px rgba(49, 80, 121, 0.07);
}

.process-list h3 {
  margin: 24px 0 0;
  color: var(--ink);
  font-size: 19px;
}

.process-list p {
  max-width: 220px;
  margin: 11px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.75;
}

.cooperation-section {
  padding-top: 0;
  background: var(--surface);
}

.cooperation-panel {
  position: relative;
  display: grid;
  min-height: 410px;
  overflow: hidden;
  padding: 72px 78px;
  border-radius: 8px;
  background: #0d2a55;
  grid-template-columns: 1.05fr 0.95fr;
}

.cooperation-panel::after {
  position: absolute;
  right: -120px;
  bottom: -260px;
  width: 620px;
  height: 620px;
  border: 1px solid rgba(91, 186, 255, 0.16);
  border-radius: 50%;
  content: "";
}

.cooperation-copy {
  position: relative;
  z-index: 2;
}

.cooperation-copy h2 {
  color: #fff;
}

.cooperation-copy > p {
  max-width: 590px;
  margin: 18px 0 0;
  color: #b7c6d8;
  font-size: 15px;
  line-height: 1.85;
}

.cooperation-points {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin: 24px 0 30px;
}

.cooperation-points span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #d9e7f5;
  font-size: 13px;
}

.cooperation-points .anticon {
  color: #63c2ff;
}

.cooperation-panel > img {
  position: absolute;
  z-index: 1;
  right: 54px;
  bottom: 0;
  width: 410px;
  max-height: 390px;
  object-fit: contain;
}

.trust-section {
  background: #fff;
}

.business-data {
  display: grid;
  padding: 0 0 76px;
  border-bottom: 1px solid var(--line);
  grid-template-columns: repeat(4, 1fr);
}

.business-data > div {
  padding-left: 30px;
  border-left: 1px solid var(--line);
}

.business-data span {
  display: block;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}

.business-data strong {
  display: block;
  margin-top: 12px;
  color: var(--ink);
  font-size: 34px;
}

.business-data small {
  margin-left: 4px;
  font-size: 12px;
}

.trust-grid {
  display: grid;
  margin-top: 86px;
  grid-template-columns: 1.25fr 0.75fr;
  gap: 110px;
}

.qualification-block .section-heading {
  max-width: 580px;
}

.certificate-list {
  display: flex;
  gap: 18px;
  margin-top: 36px;
}

.certificate-list button {
  position: relative;
  width: 220px;
  height: 294px;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #f7f9fc;
  cursor: zoom-in;
}

.certificate-list img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  transition: transform 0.3s ease;
}

.certificate-list button:hover img {
  transform: scale(1.025);
}

.certificate-list span {
  position: absolute;
  right: 10px;
  bottom: 10px;
  left: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(7, 26, 54, 0.82);
  color: #fff;
  font-size: 11px;
}

.partner-block {
  padding: 34px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.partner-block h3 {
  margin: 13px 0 0;
  color: var(--ink);
  font-size: 28px;
}

.partner-block > p {
  margin: 13px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.75;
}

.partner-grid {
  display: grid;
  margin-top: 30px;
  border-top: 1px solid var(--line);
  border-left: 1px solid var(--line);
  grid-template-columns: repeat(2, 1fr);
}

.partner-grid > div {
  display: flex;
  height: 100px;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  background: #fff;
}

.partner-grid img {
  width: 74px;
  height: 74px;
  object-fit: contain;
}

.site-footer {
  padding: 72px 0 24px;
  background: #06152d;
  color: #fff;
}

.footer-main {
  display: grid;
  grid-template-columns: 1.05fr 1.35fr 0.45fr;
  gap: 70px;
}

.footer-brand img {
  width: 152px;
  padding: 5px;
  border-radius: 4px;
  background: #fff;
}

.footer-brand p {
  max-width: 380px;
  margin: 20px 0 0;
  color: #8fa2ba;
  font-size: 13px;
  line-height: 1.85;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.footer-links > div {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 12px;
}

.footer-links h3 {
  margin: 0 0 10px;
  color: #fff;
  font-size: 14px;
}

.footer-links a,
.footer-links button,
.footer-contact span {
  padding: 0;
  background: transparent;
  color: #8fa2ba;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.6;
}

.footer-links a:hover,
.footer-links button:hover {
  color: #fff;
}

.footer-contact strong {
  color: #d9e7f5;
  font-size: 14px;
}

.footer-code {
  justify-self: end;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 136px;
  text-align: center;
}

.footer-code-card {
  width: 120px;
  height: 120px;
  padding: 8px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.14);
}

.footer-code img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.footer-code span {
  display: block;
  width: 120px;
  margin-top: 12px;
  color: #c4d3e5;
  font-size: 12px;
  line-height: 1.4;
}

.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 54px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #72859e;
  font-size: 11px;
}

.footer-bottom a:hover {
  color: #fff;
}

.floating-actions {
  position: fixed;
  z-index: 80;
  right: 24px;
  bottom: 24px;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 10px;
}

.consult-fab,
.top-fab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(14, 52, 105, 0.2);
}

.consult-fab {
  width: 62px;
  height: 62px;
  background: var(--blue);
  color: #fff;
  flex-direction: column;
  gap: 3px;
}

.consult-fab .anticon {
  font-size: 22px;
}

.consult-fab span {
  font-size: 10px;
  font-weight: 700;
}

.top-fab {
  width: 42px;
  height: 42px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  font-size: 17px;
}

.consult-content {
  padding: 12px 10px 8px;
  text-align: center;
}

.modal-kicker {
  color: var(--blue);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.consult-content h2 {
  margin: 12px 0 0;
  color: var(--ink);
  font-size: 25px;
}

.consult-content p {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 13px;
}

.modal-code {
  width: 210px;
  height: 210px;
  margin: 24px auto 18px;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
}

.modal-code img {
  width: 100%;
  height: 100%;
}

.modal-phone {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--ink);
  font-size: 15px;
  font-weight: 800;
}

.certificate-preview {
  display: block;
  width: 100%;
  max-height: 78vh;
  object-fit: contain;
}

.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes visual-enter {
  from {
    opacity: 0;
    transform: translate(-43%, -46%) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translate(-47%, -49%) scale(1);
  }
}

@media (max-width: 1180px) {
  .container {
    width: calc(100% - 48px);
  }

  .desktop-nav {
    gap: 20px;
  }

  .desktop-only {
    display: none;
  }

  .hero-grid {
    grid-template-columns: minmax(490px, 0.95fr) minmax(0, 1.05fr);
  }

  .hero-copy {
    padding-left: 0;
  }

  .hero-copy h1 {
    font-size: 54px;
  }

  .hero-visual > img {
    width: 740px;
  }

  .feature-content {
    max-width: 52%;
  }

  .advantage-layout {
    grid-template-columns: minmax(430px, 0.9fr) minmax(0, 1.1fr);
    gap: 72px;
  }

  .advantage-intro h2 {
    font-size: 45px;
  }

  .cooperation-panel > img {
    right: -10px;
    width: 370px;
  }

  .trust-grid {
    gap: 50px;
  }
}

@media (max-width: 980px) {
  .container {
    width: min(100% - 36px, 760px);
  }

  .section {
    padding: 84px 0;
  }

  .desktop-nav,
  .nav-cta {
    display: none;
  }

  .menu-button {
    display: inline-flex;
  }

  .mobile-menu {
    position: absolute;
    top: 80px;
    right: 18px;
    left: 18px;
    display: grid;
    padding: 12px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 20px 50px rgba(18, 46, 84, 0.14);
  }

  .mobile-menu a,
  .mobile-menu button {
    padding: 14px 12px;
    border-bottom: 1px solid #edf1f6;
    background: #fff;
    color: var(--ink);
    text-align: left;
    font-size: 14px;
    font-weight: 700;
  }

  .mobile-menu button {
    border-bottom: 0;
    color: var(--blue);
  }

  .hero-section {
    min-height: auto;
    padding-top: 120px;
  }

  .hero-section::before {
    display: none;
  }

  .hero-grid {
    display: flex;
    min-height: auto;
    flex-direction: column;
    gap: 20px;
  }

  .hero-copy {
    width: 100%;
    padding-bottom: 0;
  }

  .hero-copy h1 {
    font-size: 58px;
  }

  .hero-description {
    max-width: 620px;
  }

  .hero-visual {
    width: 100%;
    min-height: 520px;
  }

  .hero-visual > img {
    width: min(780px, 105vw);
  }

  .hero-stats {
    min-height: 190px;
    padding: 20px;
    grid-template-columns: repeat(2, 1fr);
  }

  .hero-stats > div:not(:last-child)::after {
    display: none;
  }

  .hero-stats > div {
    min-height: 74px;
  }

  .split-heading {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .split-heading p {
    max-width: 620px;
  }

  .carrier-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .feature-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .feature-card-large {
    grid-column: span 2;
  }

  .advantage-layout,
  .trust-grid {
    grid-template-columns: 1fr;
    gap: 58px;
  }

  .advantage-intro h2 {
    font-size: 46px;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .pricing-card {
    min-height: auto;
  }

  .plan-head p {
    min-height: auto;
  }

  .process-list {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 46px;
  }

  .process-list article:nth-child(2) .step-line {
    display: none;
  }

  .cooperation-panel {
    min-height: 610px;
    padding: 58px;
    grid-template-columns: 1fr;
  }

  .cooperation-copy {
    max-width: 600px;
  }

  .cooperation-panel > img {
    right: 50%;
    width: 370px;
    transform: translateX(50%);
  }

  .business-data {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 36px;
  }

  .footer-main {
    grid-template-columns: 1fr;
    gap: 42px;
  }

  .footer-code {
    justify-self: start;
  }
}

@media (max-width: 600px) {
  .container {
    width: calc(100% - 32px);
  }

  .section {
    padding: 72px 0;
  }

  .site-header {
    height: 68px;
  }

  .brand {
    width: 118px;
  }

  .mobile-menu {
    top: 68px;
  }

  .hero-section {
    padding-top: 104px;
  }

  .eyebrow {
    margin-bottom: 18px;
  }

  .hero-copy h1 {
    font-size: 38px;
    line-height: 1.18;
  }

  .hero-description {
    margin-top: 20px;
    font-size: 16px;
    line-height: 1.75;
  }

  .hero-actions {
    margin-top: 27px;
  }

  .hero-actions button {
    min-width: 0;
    flex: 1;
  }

  .hero-proof {
    gap: 12px 16px;
    margin-top: 24px;
  }

  .hero-proof span {
    font-size: 12px;
  }

  .hero-visual {
    min-height: 360px;
  }

  .hero-visual > img {
    width: 520px;
    transform: translate(-48%, -48%);
  }

  .visual-orbit {
    display: none;
  }

  .hero-stats {
    padding: 14px 4px;
  }

  .hero-stats strong {
    font-size: 23px;
  }

  .hero-stats span {
    font-size: 11px;
  }

  .section-heading h2,
  .advantage-intro h2,
  .cooperation-copy h2 {
    font-size: 32px;
  }

  .advantage-intro h2 span {
    white-space: normal;
  }

  .section-heading p {
    font-size: 14px;
  }

  .carrier-grid {
    margin-top: 38px;
    grid-template-columns: repeat(2, 1fr);
  }

  .carrier-item {
    min-height: 118px;
  }

  .feature-grid {
    margin-top: 42px;
    grid-template-columns: 1fr;
  }

  .feature-card,
  .feature-card-large {
    min-height: 260px;
    grid-column: span 1;
  }

  .feature-card-large {
    min-height: 390px;
  }

  .feature-content {
    right: 26px;
    bottom: 26px;
    left: 26px;
    max-width: none;
  }

  .mini-chart,
  .deployment-status {
    top: 88px;
    right: 26px;
    bottom: auto;
  }

  .feature-card {
    padding: 26px;
  }

  .feature-index {
    top: 27px;
    right: 26px;
  }

  .advantage-layout {
    gap: 46px;
  }

  .advantage-list article {
    grid-template-columns: 40px 1fr;
    gap: 10px;
  }

  .pricing-grid {
    margin-top: 42px;
  }

  .pricing-card {
    padding: 28px 24px;
  }

  .process-list {
    margin-top: 50px;
    grid-template-columns: 1fr;
    gap: 34px;
  }

  .process-list article {
    display: grid;
    padding: 0;
    grid-template-columns: 54px 1fr;
    column-gap: 16px;
  }

  .step-number {
    display: none;
  }

  .step-line {
    top: 52px;
    bottom: -34px;
    left: 25px;
    width: 1px;
    height: auto;
  }

  .process-list article:nth-child(2) .step-line {
    display: block;
  }

  .process-list article:last-child .step-line {
    display: none;
  }

  .step-icon {
    margin-top: 0;
    grid-row: span 2;
  }

  .process-list h3 {
    margin: 2px 0 0;
  }

  .process-list p {
    max-width: none;
    margin-top: 7px;
  }

  .cooperation-panel {
    min-height: 660px;
    padding: 42px 28px;
  }

  .cooperation-points {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .cooperation-panel > img {
    width: 320px;
  }

  .business-data {
    padding-bottom: 50px;
    gap: 30px 0;
  }

  .business-data > div {
    padding-left: 16px;
  }

  .business-data strong {
    font-size: 23px;
  }

  .trust-grid {
    margin-top: 58px;
  }

  .certificate-list {
    gap: 10px;
  }

  .certificate-list button {
    width: 50%;
    height: 246px;
  }

  .partner-block {
    padding: 26px 22px;
  }

  .partner-grid > div {
    height: 86px;
  }

  .footer-links {
    grid-template-columns: repeat(2, 1fr);
  }

  .footer-contact {
    grid-column: span 2;
  }

  .footer-code {
    align-items: center;
    justify-self: center;
    width: 100%;
  }

  .footer-bottom {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
    line-height: 1.6;
  }

  .floating-actions {
    right: 14px;
    bottom: 14px;
  }

  .consult-fab {
    width: 54px;
    height: 54px;
  }

  .consult-fab span {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
